import type { FC } from "react";
import type { DashboardData } from "../../../types";

interface CardsProps {
  data: DashboardData;
  isLoading: boolean;
}

const Cards: FC<CardsProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="animate-pulse bg-muted/50 rounded-xl h-40" />
        ))}
      </section>
    );
  }

  // Format revenue as Naira
  const formatNaira = (value: string | number) => {
    // Handle potential invalid inputs
    if (value === null || value === undefined || isNaN(Number(value))) {
      return "USD0.00";
    }

    // Convert to number first (handles both string and number inputs)
    const numValue = Number(value);

    // Format with commas and two decimal places
    return `USD${numValue.toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const cards = [
    {
      title: "Total Planned Visits",
      value: data.total_planned_visits.total.toString(),
      percentage: `${data.total_planned_visits.percentage_change}% from last month`,
      trend: data.total_planned_visits.percentage_change >= 0 ? "up" : "down",
    },
    {
      title: "Total Achieved Visits",
      value: data.total_achieved_visits.total.toString(),
      percentage: `${data.total_achieved_visits.percentage_change}% from last week`,
      trend: data.total_achieved_visits.percentage_change >= 0 ? "up" : "down",
    },
    {
      title: "Call Rate",
      value: `${data.total_call_rate.total.call_rate.toFixed(2)}%`,
      trend: "neutral",
    },
    {
      title: "Total Companies",
      value: data.total_companies.toString(),
      trend: "neutral",
    },
    {
      title: "Total Managers",
      value: data.total_managers.toString(),
      trend: "neutral",
    },
    {
      title: "Total Sales Reps",
      value: data.total_sales_rep.toString(),
      trend: "neutral",
    },
    {
      title: "Revenue",
      value: formatNaira(data.revenue),
      trend: "neutral",
    },
  ];

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
      {cards.map((card, index) => (
        <div
          key={index}
          className="bg-card rounded-xl border shadow-sm p-6 hover:shadow-lg transition-all duration-200 ease-in-out"
        >
          <h3 className="text-base font-medium text-muted-foreground">
            {card.title}
          </h3>
          <div className="mt-4 space-y-2">
            <p className="text-3xl font-bold text-foreground">{card.value}</p>
            {card.percentage && (
              <p
                className={`text-sm font-medium ${
                  card.trend === "up"
                    ? "text-emerald-600"
                    : card.trend === "down"
                    ? "text-red-600"
                    : "text-muted-foreground"
                }`}
              >
                {card.percentage}
              </p>
            )}
          </div>
        </div>
      ))}
    </section>
  );
};

export default Cards;
